import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { useTinycmsSites } from './sites';
import { useAddons } from './addons';
import { InkSection } from './shared';
import { ReaderQuestions } from './ReaderQuestions';
import { TaxonomySettings } from '../settings/Taxonomy';
import { SeeProCta, usePrismSwitch } from './Sections';

/**
 * Prism AI → Settings (Prototype › Prism AI › Settings, backlog 8938bafb):
 * everything Prism AI in one place — whether it's on, what it reads, reader
 * questions, and the taxonomy it classifies facts with (moved here from
 * Content's article settings). Opened from the Settings button on the right
 * of the Prism AI header, like Content's; "← Prism AI" goes back to the view
 * it came from. No Research / Write toggle here.
 */

/** What Prism AI reads, and never reads (atomizer/services/pageFacts.js). */
const READS: Array<[string, string, string]> = [
  ['Articles', 'Every published article, again after each new version', 'Read'],
  ['Pages', 'Live pages only. Drafts are read when they go live.', 'Read'],
  [
    'Article lists on pages',
    'Each article in a list is read on its own, not as part of the page',
    'Read separately',
  ],
  ['Landing pages', 'Campaign copy, so nothing on them becomes a fact', 'Never'],
  ['Forms and responses', 'Nothing readers send through a form is read', 'Never'],
];

export function PrismAiSettings() {
  const { accountId, selectedAccount } = useAccount();
  const { atomizeOn, atomizeHeld, readsOn, isOwner, ownerEmail } = useAddons();
  const { sites } = useTinycmsSites();
  const { state } = useLocation() as { state?: { from?: string } | null };
  const back =
    state?.from && state.from.startsWith('/prism-ai/') ? state.from : '/prism-ai/research';
  const siteName = selectedAccount?.name || 'this site';
  const siteUrl = sites.find((s) => s.accountId === accountId)?.siteUrl || null;
  const { chatOn, switching, error, switchTo } = usePrismSwitch();
  // "Turn off" asks in a popover under the button (round 13: window.confirm was
  // refused silently in some browsers, so the button seemed to do nothing).
  const [askingOff, setAskingOff] = useState(false);
  // #reader-questions, #taxonomy (the pointers left in Content) open at that section.
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, []);

  return (
    <main className="ink-body">
      <Link to={back} className="ink-muted" style={{ textDecoration: 'none' }}>
        ← Prism AI
      </Link>
      <h1 className="ink-h1" style={{ marginTop: 10 }}>
        Prism AI settings
      </h1>
      <p className="ink-lede" style={{ paddingBottom: 12 }}>
        What Prism AI reads on {siteName}, and how readers ask it questions.
      </p>
      {error ? <p className="ink-section-error">{error}</p> : null}

      <InkSection id="prism" title="Prism AI" description="Reads and remembers what you publish.">
        {atomizeHeld ? (
          <div className="ink-prism-status">
            <div style={{ minWidth: 0 }}>
              <div className="ink-prism-status-title">
                <span
                  className="pg-dot"
                  style={{ background: readsOn ? 'var(--ink-green)' : 'var(--ink-line3)' }}
                />
                {readsOn
                  ? `On for ${siteName}`
                  : atomizeOn
                    ? `Reading is off for ${siteName}`
                    : `Off for ${siteName}`}
              </div>
              <div className="ink-prism-status-text">
                {readsOn
                  ? chatOn
                    ? 'Turning it off stops Research, Write, reader questions, and reading what you publish.'
                    : 'Turning it off stops Research, Write, and reading what you publish.'
                  : atomizeOn
                    ? 'Research and Write are on. Published articles stay unread until you turn reading on. Live pages are still read.'
                    : 'Research and Write are stopped, and nothing new is read. Turn it back on at any time.'}
              </div>
            </div>
            {!isOwner ? (
              <span className="ink-muted" style={{ fontSize: 13 }}>
                Only the site owner{ownerEmail ? `, ${ownerEmail},` : ''} can turn it{' '}
                {readsOn ? 'off' : 'on'}.
              </span>
            ) : atomizeOn ? (
              <span className="ink-prism-off" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="ink-btn"
                  disabled={switching}
                  aria-expanded={askingOff}
                  onClick={() => setAskingOff((v) => !v)}
                >
                  Turn off
                </button>
                {askingOff ? (
                  <>
                    <span className="ink-prism-off-scrim" onClick={() => setAskingOff(false)} />
                    <span
                      className="ink-prism-off-pop"
                      role="dialog"
                      aria-label="Turn off Prism AI"
                    >
                      <span className="ink-prism-off-title">Turn off Prism AI for this site?</span>
                      <span className="ink-prism-off-text">
                        {chatOn
                          ? 'Research, Write, and reader questions stop, and Prism AI stops reading what you publish. You can turn it back on at any time.'
                          : 'Research and Write stop, and Prism AI stops reading what you publish. You can turn it back on at any time.'}
                      </span>
                      <span className="ink-prism-off-actions">
                        <button
                          type="button"
                          className="ink-btn"
                          onClick={() => setAskingOff(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="ink-btn ink-btn-primary"
                          disabled={switching}
                          onClick={() => {
                            setAskingOff(false);
                            void switchTo(false);
                          }}
                        >
                          {switching ? 'Turning off…' : 'Turn off'}
                        </button>
                      </span>
                    </span>
                  </>
                ) : null}
                {!readsOn ? (
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary"
                    disabled={switching}
                    onClick={() => void switchTo(true)}
                  >
                    {switching ? 'Turning on…' : 'Turn on reading'}
                  </button>
                ) : null}
              </span>
            ) : (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={switching}
                onClick={() => void switchTo(true)}
              >
                {switching ? 'Turning on…' : 'Turn on'}
              </button>
            )}
          </div>
        ) : (
          <div className="ink-prism-status">
            <div>
              <div className="ink-prism-status-title">Prism AI comes with Pro</div>
              <div className="ink-prism-status-text">
                It reads what you publish so readers and your agent can use it.
              </div>
            </div>
            <SeeProCta />
          </div>
        )}
      </InkSection>

      <InkSection
        id="reads"
        title="What Prism AI reads"
        description={
          atomizeHeld && !readsOn
            ? atomizeOn
              ? 'Turn reading on above and published articles are read, including the ones already on the site. To remove a wrong fact, open the article or page it came from.'
              : 'Turn Prism AI on above and it reads published articles, including the ones already on the site. To remove a wrong fact, open the article or page it came from.'
            : 'It reads each new version when it goes live. To remove a wrong fact, open the article or page it came from.'
        }
      >
        <div className="ink-prism-reads">
          {READS.map(([what, note, how]) => {
            const articles = what === 'Articles' || what === 'Article lists on pages';
            const shown =
              how === 'Never' || !atomizeHeld
                ? how
                : !atomizeOn || (!readsOn && articles)
                  ? 'Off'
                  : how;
            return (
              <div key={what} className="ink-prism-read">
                <div>
                  <div className="ink-prism-read-what">{what}</div>
                  <div className="ink-prism-read-note">{note}</div>
                </div>
                <span
                  className="ink-prism-read-how"
                  data-never={shown === 'Never' || shown === 'Off' ? 'true' : undefined}
                >
                  {shown}
                </span>
              </div>
            );
          })}
        </div>
      </InkSection>

      {accountId ? <ReaderQuestions accountId={accountId} siteUrl={siteUrl} /> : null}

      <InkSection
        id="taxonomy"
        title="Taxonomy"
        description="The terms Prism AI may use when it classifies an article’s facts. It picks only from these, and skips a dimension with no terms. An article’s own topic (shown on Articles, often from its feed) is separate and isn’t changed here."
      >
        <TaxonomySettings embedded />
      </InkSection>
    </main>
  );
}
